import { toRaw } from "vue";
import dayjs from "dayjs";
import { ATTRIBUTE_TYPES } from "@/src/constants/crmConstants";
import { formatDateTime } from "@/src/utils/utils";
import { companySizeOptions } from "@/src/constants/companyConstants";
import {
  isPhoneNumberField,
  isEmailField,
  formatSelectTimestampValue,
  customFilterOption,
} from "@/src/utils/detailsFieldUtils";

export const SPECIAL_FIELD_TYPES = {
  COUNTRY: "country",
  INDUSTRY: "industry",
  COMPANY_SIZE: "company_size",
  LIFECYCLE_STAGE: "lifecycle_stage",
  CURRENT_COMPANY: "current_company",
  LEAD_STATUS: "lead_status",
  CONTACT_STATUS: "contact_status",
  TIMEZONE: "timezone",
  COMPANY_LOCATION: "company_location",
  SOURCE: "source",
  PIPELINE: "pipeline_id",
  PIPELINE_STAGE: "pipeline_stage_id",
  DEAL_TYPE: "deal_type",
  PRIORITY: "priority",
  ANNUAL_REVENUE: "annual_revenue",
  AMOUNT: "amount",
  LIST_IDS: "list_ids",
  TAGS: "tags",
  OWNER: "owner_id",
  DEAL_OWNER: "deal_owner_id",
  LOST_REASON: "lost_reason",
};

const FIELD_TYPE_MAP = {
  country: SPECIAL_FIELD_TYPES.COUNTRY,
  industry: SPECIAL_FIELD_TYPES.INDUSTRY,
  company_size: SPECIAL_FIELD_TYPES.COMPANY_SIZE,
  lifecycle_stage: SPECIAL_FIELD_TYPES.LIFECYCLE_STAGE,
  current_company: SPECIAL_FIELD_TYPES.CURRENT_COMPANY,
  lead_status: SPECIAL_FIELD_TYPES.LEAD_STATUS,
  contact_status: SPECIAL_FIELD_TYPES.CONTACT_STATUS,
  timezone: SPECIAL_FIELD_TYPES.TIMEZONE,
  company_location: SPECIAL_FIELD_TYPES.COMPANY_LOCATION,
  source: SPECIAL_FIELD_TYPES.SOURCE,
  pipeline_id: SPECIAL_FIELD_TYPES.PIPELINE,
  pipeline_stage_id: SPECIAL_FIELD_TYPES.PIPELINE_STAGE,
  deal_type: SPECIAL_FIELD_TYPES.DEAL_TYPE,
  priority: SPECIAL_FIELD_TYPES.PRIORITY,
  annual_revenue: SPECIAL_FIELD_TYPES.ANNUAL_REVENUE,
  amount: SPECIAL_FIELD_TYPES.AMOUNT,
  list_ids: SPECIAL_FIELD_TYPES.LIST_IDS,
  tags: SPECIAL_FIELD_TYPES.TAGS,
  owner_id: SPECIAL_FIELD_TYPES.OWNER,
  deal_owner_id: SPECIAL_FIELD_TYPES.DEAL_OWNER,
  lost_reason: SPECIAL_FIELD_TYPES.LOST_REASON,
};

export function getSpecialFieldType(attribute) {
  const slugLower = String(
    attribute.slug || attribute.name || "",
  ).toLowerCase();
  const idLower = String(attribute.id || "").toLowerCase();
  return FIELD_TYPE_MAP[slugLower] || FIELD_TYPE_MAP[idLower] || null;
}

export function isSpecialField(attribute) {
  return getSpecialFieldType(attribute) !== null;
}

export function useFieldRendering(
  fieldOptions,
  statusOptionsCache,
  contactStatusOptions,
  leadStatusOptions,
  isContacts,
  isLeads,
  entity,
  fetchContactStatusOptions,
  fetchLeadStatusOptions,
) {
  const getFieldOptions = (attribute) => {
    const bySlug = fieldOptions.value[attribute.slug];
    const byId = fieldOptions.value[attribute.id];
    return bySlug || byId || [];
  };

  // Simplified field detection using the unified approach
  const isCountryField = (attribute) =>
    getSpecialFieldType(attribute) === SPECIAL_FIELD_TYPES.COUNTRY;
  const isIndustryField = (attribute) =>
    getSpecialFieldType(attribute) === SPECIAL_FIELD_TYPES.INDUSTRY;
  const isCompanySizeField = (attribute) =>
    getSpecialFieldType(attribute) === SPECIAL_FIELD_TYPES.COMPANY_SIZE;
  const isLifecycleStageField = (attribute) =>
    getSpecialFieldType(attribute) === SPECIAL_FIELD_TYPES.LIFECYCLE_STAGE;
  const isCurrentCompanyField = (attribute) =>
    getSpecialFieldType(attribute) === SPECIAL_FIELD_TYPES.CURRENT_COMPANY;
  const isLeadStatusField = (attribute) =>
    getSpecialFieldType(attribute) === SPECIAL_FIELD_TYPES.LEAD_STATUS;
  const isContactStatusField = (attribute) =>
    getSpecialFieldType(attribute) === SPECIAL_FIELD_TYPES.CONTACT_STATUS;
  const isTimezoneField = (attribute) =>
    getSpecialFieldType(attribute) === SPECIAL_FIELD_TYPES.TIMEZONE;
  const isCompanyLocationField = (attribute) =>
    getSpecialFieldType(attribute) === SPECIAL_FIELD_TYPES.COMPANY_LOCATION;
  const isSourceField = (attribute) =>
    getSpecialFieldType(attribute) === SPECIAL_FIELD_TYPES.SOURCE;
  const isAnnualRevenueField = (attribute) =>
    getSpecialFieldType(attribute) === SPECIAL_FIELD_TYPES.ANNUAL_REVENUE;
  const isPipelineField = (attribute) =>
    getSpecialFieldType(attribute) === SPECIAL_FIELD_TYPES.PIPELINE;
  const isPipelineStageField = (attribute) =>
    getSpecialFieldType(attribute) === SPECIAL_FIELD_TYPES.PIPELINE_STAGE;
  const isDealTypeField = (attribute) =>
    getSpecialFieldType(attribute) === SPECIAL_FIELD_TYPES.DEAL_TYPE;
  const isPriorityField = (attribute) =>
    getSpecialFieldType(attribute) === SPECIAL_FIELD_TYPES.PRIORITY;
  const isAmountField = (attribute) =>
    getSpecialFieldType(attribute) === SPECIAL_FIELD_TYPES.AMOUNT;

  const isSpecialSelectField = (attribute) => {
    return (
      isCountryField(attribute) ||
      isIndustryField(attribute) ||
      isCompanySizeField(attribute) ||
      isLifecycleStageField(attribute)
    );
  };

  const getSelectOptions = (attribute) => {
    const idLower = String(attribute.id || "").toLowerCase();
    const fieldKey = attribute.slug;

    if (idLower === "company_size") {
      return companySizeOptions;
    }

    if (idLower === "contact_status") {
      if (
        statusOptionsCache.contact_status &&
        statusOptionsCache.contact_status.length > 0
      ) {
        return statusOptionsCache.contact_status;
      }
      if (contactStatusOptions.value && contactStatusOptions.value.length > 0) {
        return contactStatusOptions.value;
      }
      if (isContacts.value) {
        fetchContactStatusOptions();
      }
    }

    if (idLower === "lead_status") {
      if (leadStatusOptions.value && leadStatusOptions.value.length > 0) {
        return leadStatusOptions.value;
      }
      if (isLeads.value) {
        fetchLeadStatusOptions();
      }
    }

    const optionsById = fieldOptions.value[attribute.id];
    const optionsBySlug = fieldOptions.value[fieldKey];
    const options = optionsById || optionsBySlug;

    if (options && Array.isArray(options)) {
      if (options.length > 0 && typeof options[0] === "string") {
        return options;
      }
      if (
        options.length > 0 &&
        typeof options[0] === "object" &&
        "value" in options[0]
      ) {
        return options.map((opt) => opt.value);
      }
      return options;
    }

    return attribute.options || [];
  };

  const getUserLabel = (attribute, value) => {
    if (!value) return "";

    const nameField = `${attribute.id.replace(/_id$/, "")}_name`;
    if (entity && entity[nameField]) {
      return entity[nameField];
    }

    const options = getFieldOptions(attribute);
    const match = options.find((opt) => String(opt.value) === String(value));
    return match?.label || "";
  };

  const getUserDataById = (userId, attribute) => {
    if (!userId) return {};
    const options = getFieldOptions(attribute);
    const match = options.find((opt) => String(opt.value) === String(userId));
    return match || {};
  };

  const isDateType = (type) => {
    if (!type) return false;
    const normalizedType = type.toLowerCase();
    return (
      normalizedType === ATTRIBUTE_TYPES.DATE ||
      normalizedType === ATTRIBUTE_TYPES.DATETIME
    );
  };

  const getCurrentCompanyDisplay = (companyObj) => {
    if (!companyObj) return "Select Company";
    if (typeof companyObj === "string") return companyObj;
    return (
      companyObj.name?.trim() || companyObj.website?.trim() || "Select Company"
    );
  };

  const formatDisplayValue = (attribute, value) => {
    const rawValue = toRaw(value);
    if (!rawValue) return "";

    if (attribute.id === "current_company" && typeof rawValue === "object") {
      return getCurrentCompanyDisplay(rawValue);
    }

    if (attribute.type?.toLowerCase() === ATTRIBUTE_TYPES.TIME) {
      if (dayjs.isDayjs(rawValue)) {
        return rawValue.format("HH:mm");
      }
      return rawValue;
    }

    if (isDateType(attribute.type)) {
      if (dayjs.isDayjs(rawValue)) {
        if (attribute.type.toLowerCase() === ATTRIBUTE_TYPES.DATE) {
          return rawValue.format("MM/DD/YYYY");
        } else {
          return rawValue.format("MM/DD/YYYY HH:mm");
        }
      }
      if (attribute.type.toLowerCase() === ATTRIBUTE_TYPES.DATE) {
        return formatDateTime(rawValue, "date");
      } else {
        return formatDateTime(rawValue, "full");
      }
    }

    return rawValue;
  };

  const formatCurrency = (value) => {
    if (!value) return "";
    return `$${value.toLocaleString()}`;
  };

  return {
    getFieldOptions,
    getSelectOptions,
    getUserLabel,
    getUserDataById,
    formatDisplayValue,
    formatCurrency,
    getCurrentCompanyDisplay,
    formatSelectTimestampValue,
    customFilterOption,
    isDateType,
    // Special field detection
    isCountryField,
    isIndustryField,
    isCompanySizeField,
    isLifecycleStageField,
    isCurrentCompanyField,
    isLeadStatusField,
    isContactStatusField,
    isTimezoneField,
    isCompanyLocationField,
    isSourceField,
    isAnnualRevenueField,
    isPipelineField,
    isPipelineStageField,
    isDealTypeField,
    isPriorityField,
    isAmountField,
    isSpecialSelectField,
    isPhoneNumberField,
    isEmailField,
    // Unified field type detection
    getSpecialFieldType,
    isSpecialField,
    SPECIAL_FIELD_TYPES,
  };
}
